/**
 * Notification types for AT Protocol and Mastodon notifications
 */

import type { OAuthSession } from '@atproto/oauth-client-browser'
import type { MastodonSession } from '@/lib/mastodon'
import type { PixelfedSession } from '@/lib/pixelfed'
import { strings } from '@/strings'

// --- Notification Type Enums ---

/** Unified notification types across protocols */
export type NotificationType =
    | 'like'
    | 'like_repost'
    | 'repost'
    | 'follow'
    | 'mention'
    | 'reply'
    | 'quote'
    | 'follow_request'
    | 'poll'

/** AT Protocol notification reasons */
export type ATNotificationReason =
    | 'like'
    | 'repost'
    | 'follow'
    | 'mention'
    | 'reply'
    | 'quote'
    | 'starterpack-joined'
    | 'verified'
    | 'unverified'
    | 'like-via-repost'
    | 'repost-via-repost'
    | 'subscribed-post'
    | 'contact-match'

/** Mastodon notification types */
export type MastodonNotificationType =
    | 'mention'
    | 'status'
    | 'reblog'
    | 'follow'
    | 'follow_request'
    | 'favourite'
    | 'poll'
    | 'update'
    | 'admin.sign_up'
    | 'admin.report'

/** Mastodon raw notification types that participate in grouping. */
export type MastodonGroupableType = Extract<
    MastodonNotificationType,
    'favourite' | 'reblog' | 'follow'
>

// --- Actor/Author Types ---

/** Notification actor (the person who performed the action) */
export interface NotificationActor {
    did?: string
    handle: string
    displayName?: string
    avatarUrl?: string
    /** Whether this account is bridged from ActivityPub via Bridgy Fed */
    isBridgedFromAP?: boolean
    /**
     * Whether the viewer follows this actor (AT Protocol only).
     * Populated from `author.viewer.following` on the notification response.
     * Used to detect mutual follow-backs so they don't get grouped.
     */
    viewerIsFollowing?: boolean
}

// --- Subject Types ---

/** The subject of the notification (post that was liked, etc.) */
export interface NotificationSubject {
    uri: string
    cid?: string
    text?: string
    createdAt?: string
}

// --- Unified Notification Type ---

/** Unified notification used by all UI components */
export interface Notification {
    id: string
    uri: string
    type: NotificationType
    /**
     * The raw AT Protocol reason for this notification, when protocol is atproto.
     * Preserved so that grouping can distinguish reasons that collapse to the same
     * unified `type` (e.g. `repost` vs `repost-via-repost`, `reply` vs `subscribed-post`).
     */
    atReason?: ATNotificationReason
    /**
     * The raw Mastodon notification type, when protocol is mastodon or pixelfed.
     * Preserved (like `atReason` for AT Proto) so that grouping can operate on
     * the raw type rather than the collapsed unified `type`.
     */
    mastodonType?: MastodonNotificationType
    actor: NotificationActor
    subject?: NotificationSubject
    createdAt: string
    isRead: boolean
    protocol: 'atproto' | 'mastodon' | 'pixelfed'
}

/**
 * A group of similar notifications collapsed into one row.
 * Matches the Bluesky reference algorithm: notifications with the same raw
 * reason + subject within a 48-hour window are grouped. Applies to AT Protocol
 * reasons and to Mastodon/Pixelfed favourite/reblog/follow types.
 */
export interface GroupedNotification {
    kind: 'grouped'
    /** The raw protocol reason groups match on: AT reason, or raw Mastodon type. */
    reason: ATNotificationReason | MastodonGroupableType
    /** Mapped unified type for shared UI helpers (icons, action text). */
    type: NotificationType
    actors: NotificationActor[]
    /** Subject is absent for `follow` groups (follows have no reasonSubject). */
    subject?: NotificationSubject
    createdAt: string
    isRead: boolean
    protocol: Notification['protocol']
    notifications: Notification[]
}

/** Display item: either a single notification or a grouped notification */
export type NotificationDisplayItem = Notification | GroupedNotification

/** Type guard for grouped notifications */
export function isGroupedNotification(item: NotificationDisplayItem): item is GroupedNotification {
    return 'kind' in item && item.kind === 'grouped'
}

/** Paginated notifications response */
export interface NotificationsPage {
    notifications: Notification[]
    cursor?: string
    seenAt?: string
}

/** Unread count response */
export interface UnreadCountResult {
    count: number
}

// --- AT Protocol Response Types ---

/** AT Protocol notification from API */
export interface ATNotification {
    uri: string
    cid: string
    author: {
        did: string
        handle: string
        displayName?: string
        avatar?: string
    }
    reason: ATNotificationReason
    reasonSubject?: string
    record: Record<string, unknown>
    isRead: boolean
    indexedAt: string
    labels?: Array<{
        val: string
        src: string
    }>
}

/** AT Protocol list notifications response */
export interface ATListNotificationsResponse {
    notifications: ATNotification[]
    cursor?: string
    seenAt?: string
}

/** AT Protocol unread count response */
export interface ATUnreadCountResponse {
    count: number
}

// --- Mastodon Response Types ---

/** Mastodon account (actor) */
export interface MastodonAccount {
    id: string
    username: string
    acct: string
    display_name: string
    avatar: string
    avatar_static: string
    url: string
}

/** Mastodon status (post) */
export interface MastodonStatus {
    id: string
    uri: string
    content: string
    created_at: string
    account: MastodonAccount
}

/** Mastodon notification from API */
export interface MastodonNotification {
    id: string
    type: MastodonNotificationType
    created_at: string
    account: MastodonAccount
    status?: MastodonStatus
}

/** Mastodon notifications response (array) */
export type MastodonNotificationsResponse = MastodonNotification[]

/** Mastodon marker for tracking read position */
export interface MastodonMarker {
    last_read_id: string
    version: number
    updated_at: string
}

/** Mastodon markers API response */
export interface MastodonMarkerResponse {
    notifications?: MastodonMarker
}

// --- Service Function Types ---

/** Options for fetching notifications */
export interface FetchNotificationsOptions {
    limit?: number
    cursor?: string
}

/** Sessions needed for notification fetching */
export interface NotificationSessions {
    atSession?: OAuthSession | null
    mastodonSession?: MastodonSession | PixelfedSession | null
}

// --- Mapping Helpers ---

/** Map AT Protocol reason to unified type */
export function mapATReasonToType(reason: ATNotificationReason): NotificationType {
    switch (reason) {
        case 'like':
            return 'like'
        case 'like-via-repost':
            return 'like_repost'
        case 'repost':
        case 'repost-via-repost':
            return 'repost'
        case 'follow':
            return 'follow'
        case 'mention':
            return 'mention'
        case 'reply':
        case 'subscribed-post':
            return 'reply'
        case 'quote':
            return 'quote'
        default:
            return 'mention' // fallback for unknown types
    }
}

/** Map Mastodon type to unified type */
export function mapMastodonTypeToType(type: MastodonNotificationType): NotificationType {
    switch (type) {
        case 'favourite':
            return 'like'
        case 'reblog':
            return 'repost'
        case 'follow':
            return 'follow'
        case 'follow_request':
            return 'follow_request'
        case 'mention':
        case 'status':
            return 'mention'
        case 'poll':
            return 'poll'
        default:
            return 'mention' // fallback for unknown types
    }
}

/** Get display text for notification type */
export function getNotificationText(
    type: NotificationType,
    protocol?: 'atproto' | 'mastodon' | 'pixelfed'
): string {
    const isActivityPub = protocol === 'mastodon' || protocol === 'pixelfed'
    switch (type) {
        case 'like':
            return strings.notifications.liked
        case 'like_repost':
            return strings.notifications.likedRepost
        case 'repost':
            return isActivityPub ? strings.notifications.boosted : strings.notifications.reposted
        case 'follow':
            return strings.notifications.followed
        case 'follow_request':
            return strings.notifications.followRequested
        case 'mention':
            return strings.notifications.mentioned
        case 'reply':
            return strings.notifications.replied
        case 'quote':
            return strings.notifications.quoted
        case 'poll':
            return strings.notifications.pollEnded
        default:
            return strings.notifications.interacted
    }
}
